import React, { useCallback, useEffect, useRef } from 'react';

import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Button,
  Dialog,
  DialogFooter,
  Form,
  FormField,
  FormControl,
  FormItem,
  TagInput,
} from '@sandtech-hub-monorepo/ui-kit';

import { UpdateSkillsDocument } from '@sandtech-hub-monorepo/ehub-client';

const formSchema = z.object({
  skills: z.array(z.string()),
});

/**
 * Broad on purpose: real skills carry punctuation a plain letters-only regex
 * would reject - "C++", "C#", "Node.js", "CI/CD", "React (Native)",
 * "UX/UI", "R&D". Only whitespace-only entries and control characters are
 * actually rejected.
 */
const validateSkill = (skill: string) =>
  /^[\p{L}\p{N}][\p{L}\p{N}&.+#/()'’\- ]*$/u.test(skill)
    ? null
    : "Only letters, numbers, spaces and & . + # / ( ) ' - are allowed";

export const SkillsSectionEditDialog = ({
  open,
  defaultValues,
  handleOpenChange,
}: {
  open: boolean;
  defaultValues: string[];
  handleOpenChange: (open: boolean) => void;
}) => {
  const [updateSkills, { loading }] = useMutation(UpdateSkillsDocument);
  const inputValueRef = useRef<string>();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        form.reset();
      }
      handleOpenChange(open);
    },
    [form, handleOpenChange]
  );

  useEffect(() => {
    form.reset({ skills: defaultValues });
  }, [defaultValues, form]);

  const onSubmit = async ({ skills }: z.infer<typeof formSchema>) => {
    const input = {
      skills,
    };
    if (inputValueRef.current) {
      input.skills.push(inputValueRef.current);
    }
    await updateSkills({ variables: { input } });

    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      className="bg-card"
      title="Skills"
      description="Add your skills"
    >
      <Form {...form}>
        <form
          className="flex flex-1 flex-col gap-4 pt-4 justify-between"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="skills"
            render={({ field }) => (
              <FormItem className="flex items-center gap-2">
                <FormControl>
                  <TagInput
                    {...field}
                    inputValueRef={inputValueRef}
                    placeholder="Add a skill"
                    tags={form.getValues().skills}
                    setTags={(newTags) => {
                      form.setValue('skills', newTags as [string, ...string[]]);
                    }}
                    maxItems={20}
                    maxLength={40}
                    validate={validateSkill}
                    tagClassName="font-semibold"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <DialogFooter className="mt-auto flex flex-col sm:flex-row gap-1">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? <Loader2Icon className="animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </Dialog>
  );
};
